/**
 * Conversation store: one object, immutable updates, rendered by Preact.
 * Flow logic comes from @wapadesk/engine; this file wires it to the API.
 */
import { useEffect, useReducer, useRef } from "preact/hooks";
import type {
  BookingOptionsResponse,
  BookingSummary,
  InitPayload,
  MessageTemplate,
  SlotsResponse,
  WidgetConfigResponse,
} from "@wapadesk/schema";
import {
  applyAnswer,
  flowState,
  validateAnswer,
  type Answers,
  type AnswerValue,
  type FlatNode,
  type FlowState,
} from "@wapadesk/engine";
import { AnswerQueue, ApiClient, ApiClientError } from "./lib/api";
import { send } from "./lib/bridge";
import { Tracker } from "./lib/track";

export interface UploadedFile {
  fileId: string;
  nodeId: string;
  name: string;
  size: number;
  contentType: string;
  status: "uploading" | "done" | "failed";
}

/** A booking made or found in this conversation (section 11). */
export interface BookedSummary {
  typeName: string;
  doctorName: string;
  startAt: string;
  endAt: string;
  timezone: string;
  mode: "online" | "in_person";
  status: string;
  address?: string;
  location?: { name: string; address: string };
  meetingUrl?: string;
  procedures?: string[];
  calendar?: { google: string; ics: string };
}

export interface PaymentState {
  appointmentId: string;
  clientSecret: string;
  stripeAccountId: string;
  publishableKey: string;
  amount: number;
  currency: string;
  holdExpiresAt: string;
  /** "pay": element shown; "confirming": waiting for the webhook; "failed" / "expired". */
  phase: "pay" | "confirming" | "failed" | "expired";
}

export interface BookingState {
  open: boolean;
  busy: boolean;
  payment?: PaymentState;
  options?: BookingOptionsResponse;
  typeId?: string;
  /** Procedures the patient picked (catalogue names); `proceduresDone` once the step was passed. */
  procedures?: string[];
  proceduresDone?: boolean;
  /** Chosen location for in-person types at a clinic with several sites. */
  locationId?: string;
  doctorId?: string;
  from?: string;
  slots?: SlotsResponse;
  booked?: BookedSummary;
  error?: string;
}

export interface State {
  phase: "loading" | "ready" | "fallback" | "error";
  config?: WidgetConfigResponse;
  init?: InitPayload;
  answers: Answers;
  flow?: FlowState;
  /** Node currently open for input (next question or an edited one). */
  activeNodeId?: string;
  editingNodeId?: string;
  shownUpTo: number; // transcript entries revealed (typing animation)
  consented: boolean;
  ref?: string;
  files: UploadedFile[];
  helpOpen?: string;
  error?: string;
  saving: number;
  handoff?: {
    type: "whatsapp" | "booking" | "email";
    url: string;
    message?: string;
    label?: string;
  };
  whatsappOpened: boolean;
  booking?: BookingState;
}

type Action =
  | { type: "config"; config: WidgetConfigResponse; init: InitPayload }
  | { type: "fallback"; config?: WidgetConfigResponse; init?: InitPayload }
  | { type: "error"; message: string }
  | {
      type: "resume";
      answers: Answers;
      files: UploadedFile[];
      ref: string;
      whatsappOpened: boolean;
      appointment?: BookedSummary;
    }
  | { type: "answer"; nodeId: string; value: AnswerValue }
  | { type: "skip"; nodeId: string }
  | { type: "edit"; nodeId: string | undefined }
  | { type: "reveal" }
  | { type: "consented"; ref: string }
  | { type: "help"; nodeId: string | undefined }
  | { type: "file"; file: UploadedFile }
  | { type: "fileStatus"; fileId: string; status: UploadedFile["status"] }
  | { type: "saving"; delta: number }
  | { type: "handoff"; handoff: State["handoff"] }
  | { type: "whatsappOpened" }
  | { type: "booking"; patch: Partial<BookingState> };

const initial: State = {
  phase: "loading",
  answers: {},
  shownUpTo: 0,
  consented: false,
  files: [],
  saving: 0,
  whatsappOpened: false,
};

function withFlow(s: State, answers: Answers): State {
  const form = s.config?.form?.schema;
  if (!form) return { ...s, answers };
  const flow = flowState(form, answers);
  return { ...s, answers, flow, activeNodeId: flow.next?.node.id };
}

function reducer(s: State, a: Action): State {
  switch (a.type) {
    case "config":
      return withFlow(
        {
          ...s,
          phase: a.config.mode === "active" && a.config.form ? "ready" : "fallback",
          config: a.config,
          init: a.init,
        },
        s.answers,
      );
    case "fallback":
      return {
        ...s,
        phase: "fallback",
        ...(a.config ? { config: a.config } : {}),
        ...(a.init ? { init: a.init } : {}),
      };
    case "error":
      return { ...s, phase: "error", error: a.message };
    case "resume": {
      const next = withFlow(
        {
          ...s,
          consented: true,
          ref: a.ref,
          files: a.files,
          whatsappOpened: a.whatsappOpened,
          ...(a.appointment
            ? { booking: { open: false, busy: false, booked: a.appointment } }
            : {}),
        },
        a.answers,
      );
      return { ...next, shownUpTo: next.flow?.transcript.length ?? 0 };
    }
    case "answer": {
      const form = s.config?.form?.schema;
      if (!form) return s;
      const { answers } = applyAnswer(form, s.answers, a.nodeId, a.value);
      const next = withFlow({ ...s, editingNodeId: undefined }, answers);
      return next;
    }
    case "skip": {
      const next = withFlow({ ...s, editingNodeId: undefined }, { ...s.answers, [a.nodeId]: "" });
      return next;
    }
    case "edit":
      return { ...s, editingNodeId: a.nodeId, activeNodeId: a.nodeId ?? s.flow?.next?.node.id };
    case "booking":
      return { ...s, booking: { open: false, busy: false, ...s.booking, ...a.patch } };
    case "reveal":
      return { ...s, shownUpTo: s.shownUpTo + 1 };
    case "consented":
      return { ...s, consented: true, ref: a.ref };
    case "help":
      return { ...s, helpOpen: a.nodeId };
    case "file":
      return { ...s, files: [...s.files.filter((f) => f.fileId !== a.file.fileId), a.file] };
    case "fileStatus":
      return {
        ...s,
        files: s.files.map((f) => (f.fileId === a.fileId ? { ...f, status: a.status } : f)),
      };
    case "saving":
      return { ...s, saving: Math.max(0, s.saving + a.delta) };
    case "handoff":
      return { ...s, handoff: a.handoff };
    case "whatsappOpened":
      return { ...s, whatsappOpened: true };
  }
}

export interface Services {
  api: ApiClient;
  tracker: Tracker | undefined;
  queue: AnswerQueue | undefined;
}

export function useConversation(apiOrigin: string) {
  const [state, dispatch] = useReducer(reducer, initial);
  const svc = useRef<Services>({
    api: new ApiClient(apiOrigin),
    tracker: undefined,
    queue: undefined,
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  // Boot: wait for the loader's init (or standalone), fetch config, resume if a token exists.
  useEffect(() => {
    let cancelled = false;
    const onInit = async (raw: InitPayload) => {
      // Live funnel preview: the draft from the dashboard, an in-memory API, nothing stored.
      if (isPreview() && previewPayload)
        svc.current.api = new PreviewApi(
          previewPayload.config as WidgetConfigResponse,
          previewPayload.template as Partial<MessageTemplate> | undefined,
        );
      const api = svc.current.api;
      try {
        // A share link carries the slug; the config answers with the clinic id.
        const config = raw.slug
          ? await api.configBySlug(raw.slug, raw.context.pageUrl)
          : await api.config(raw.clinicId, raw.context.pageUrl);
        if (cancelled) return;
        const init: InitPayload = raw.slug ? { ...raw, clinicId: config.clinicId } : raw;
        const path = (() => {
          try {
            return new URL(init.context.pageUrl).pathname;
          } catch {
            return undefined;
          }
        })();
        svc.current.tracker = new Tracker(api, {
          clinicId: init.clinicId,
          ...(init.context.visitId ? { visitId: init.context.visitId } : {}),
          ...(path ? { pagePath: path } : {}),
          ...(init.context.device ? { device: init.context.device } : {}),
          ...(init.locale ? { locale: init.locale } : {}),
          ...(config.form
            ? { formId: config.form.formId, formVersionId: config.form.formVersionId }
            : {}),
          ...(init.context.utm ? { utm: init.context.utm } : {}),
        });
        svc.current.queue = new AnswerQueue(api, (nodeId, r) => {
          dispatch({ type: "saving", delta: -1 });
          if (r instanceof ApiClientError) {
            if (r.status === 401) dispatch({ type: "error", message: "session_expired" });
            else dispatch({ type: "error", message: r.code });
            return;
          }
          const flow = stateRef.current.flow;
          if (r.completed && flow?.completed) {
            svc.current.tracker?.track("form_completed");
            send({ source: "wapadesk", type: "track", payload: { event: "lead_completed" } });
          }
          void nodeId;
        });
        dispatch({ type: "config", config, init });
        if (init.sessionToken && config.mode === "active") {
          api.token = init.sessionToken;
          try {
            const r = await api.resume();
            if (cancelled) return;
            // A conversation continues on the version it started on, even after the clinic
            // published a new one (the answers endpoint validates against that version).
            const sameVersion = r.formVersionId === config.form?.formVersionId;
            if (sameVersion || r.schema) {
              if (!sameVersion)
                dispatch({
                  type: "config",
                  config: {
                    ...config,
                    form: { formId: r.formId, formVersionId: r.formVersionId, schema: r.schema! },
                  },
                  init,
                });
              dispatch({
                type: "resume",
                answers: r.answers as Answers,
                files: r.files.map((f) => ({
                  fileId: f.fileId,
                  nodeId: f.nodeId,
                  name: "",
                  size: f.size,
                  contentType: f.contentType,
                  status: f.verified ? "done" : "uploading",
                })),
                ref: r.ref,
                whatsappOpened: r.whatsappOpened,
                ...(r.appointment ? { appointment: r.appointment } : {}),
              });
              svc.current.tracker?.track("widget_resume");
            } else {
              api.token = undefined;
              send({
                source: "wapadesk",
                type: "session",
                payload: { sessionToken: null, consented: false },
              });
            }
          } catch {
            api.token = undefined;
            send({
              source: "wapadesk",
              type: "session",
              payload: { sessionToken: null, consented: false },
            });
          }
        }
      } catch {
        if (!cancelled) dispatch({ type: "fallback", init: raw });
      }
    };
    const stop = startBridgeOnce(onInit);
    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  const actions = {
    async answer(
      node: FlatNode,
      raw: unknown,
    ): Promise<{ ok: true } | { ok: false; code: string }> {
      const s = stateRef.current;
      const form = s.config?.form?.schema;
      const config = s.config;
      if (!form || !config) return { ok: false, code: "not_ready" };
      const procedureIds = new Set(config.procedures.map((p) => p.id));
      const v = validateAnswer(node.node, raw, { procedureIds });
      if (!v.ok) return { ok: false, code: v.code };
      const wasEdit = s.answers[node.node.id] !== undefined && s.editingNodeId === node.node.id;
      if (node.node.type === "consent") {
        if (s.consented) return { ok: true };
        try {
          const r = await svc.current.api.createSession({
            clinicId: config.clinicId,
            formId: config.form!.formId,
            formVersionId: config.form!.formVersionId,
            consentNodeId: node.node.id,
            consent: v.value as Record<string, boolean>,
            locale: form.locale,
            context: s.init?.context ?? { pageUrl: location.href },
          });
          svc.current.api.token = r.sessionToken;
          dispatch({ type: "consented", ref: r.ref });
          dispatch({ type: "answer", nodeId: node.node.id, value: v.value });
          send({
            source: "wapadesk",
            type: "session",
            payload: {
              sessionToken: r.sessionToken,
              leadId: r.leadId,
              ref: r.ref,
              consented: true,
            },
          });
          svc.current.tracker?.track("consent_accepted");
          send({ source: "wapadesk", type: "track", payload: { event: "lead_started" } });
          return { ok: true };
        } catch (err) {
          const code = err instanceof ApiClientError ? err.code : "network";
          return { ok: false, code };
        }
      }
      dispatch({ type: "answer", nodeId: node.node.id, value: v.value });
      dispatch({ type: "saving", delta: 1 });
      svc.current.queue?.enqueue({ nodeId: node.node.id, value: v.value });
      svc.current.tracker?.track(wasEdit ? "answer_edited" : "node_answered", {
        nodeId: node.node.id,
        stepId: node.stepId,
        nodeType: node.node.type,
      });
      send({
        source: "wapadesk",
        type: "state",
        payload: { lastNodeId: node.node.id, stepId: node.stepId },
      });
      return { ok: true };
    },
    skip(node: FlatNode) {
      dispatch({ type: "skip", nodeId: node.node.id });
      svc.current.tracker?.track("node_answered", {
        nodeId: node.node.id,
        stepId: node.stepId,
        nodeType: node.node.type,
      });
    },
    edit(nodeId: string | undefined) {
      dispatch({ type: "edit", nodeId });
    },
    reveal() {
      dispatch({ type: "reveal" });
    },
    help(nodeId: string | undefined) {
      dispatch({ type: "help", nodeId });
      if (nodeId) svc.current.tracker?.track("help_opened", { nodeId });
    },
    declineConsent() {
      svc.current.tracker?.track("consent_declined");
      send({ source: "wapadesk", type: "close", payload: {} });
    },
    close() {
      send({ source: "wapadesk", type: "close", payload: {} });
    },
    track: (type: Parameters<Tracker["track"]>[0], data?: Parameters<Tracker["track"]>[1]) =>
      svc.current.tracker?.track(type, data),
    async upload(node: FlatNode, file: File): Promise<void> {
      const api = svc.current.api;
      const contentType = (file.type || "application/octet-stream") as "image/jpeg";
      svc.current.tracker?.track("upload_started", { nodeId: node.node.id });
      try {
        const r = await api.createUpload({
          nodeId: node.node.id,
          contentType,
          size: file.size,
          originalName: file.name.slice(0, 255),
        });
        dispatch({
          type: "file",
          file: {
            fileId: r.fileId,
            nodeId: node.node.id,
            name: file.name,
            size: file.size,
            contentType,
            status: "uploading",
          },
        });
        const put = await fetch(r.uploadUrl, { method: "PUT", headers: r.headers, body: file });
        if (!put.ok) throw new Error(String(put.status));
        dispatch({ type: "fileStatus", fileId: r.fileId, status: "done" });
        svc.current.tracker?.track("upload_completed", { nodeId: node.node.id });
      } catch (err) {
        const code = err instanceof ApiClientError ? err.code : "upload_failed";
        svc.current.tracker?.track("upload_failed", {
          nodeId: node.node.id,
          reason: code.slice(0, 60),
        });
        throw err;
      }
    },
    /** Internal booking (11.1): loads consultation types and doctors and opens the picker. */
    async openBooking(atNodeId?: string): Promise<void> {
      const s = stateRef.current;
      if (!s.consented) return;
      dispatch({ type: "booking", patch: { open: true, busy: true, error: undefined } });
      svc.current.tracker?.track("booking_click", { ...(atNodeId ? { atNodeId } : {}) });
      try {
        const options = await svc.current.api.bookingOptions();
        const bookable = options.types.filter((t) => t.bookable);
        dispatch({
          type: "booking",
          patch: {
            open: true,
            busy: false,
            options,
            typeId:
              bookable.length === 1 && options.types.length === 1 ? bookable[0]!.id : undefined,
            doctorId: undefined,
            slots: undefined,
          },
        });
      } catch {
        dispatch({ type: "booking", patch: { open: false, busy: false, error: "unavailable" } });
      }
    },
    chooseBookingProcedures(procedures: string[] | undefined) {
      dispatch({
        type: "booking",
        patch: { procedures: procedures ?? [], proceduresDone: procedures !== undefined },
      });
    },
    chooseBookingType(typeId: string | undefined) {
      dispatch({
        type: "booking",
        patch: { typeId, locationId: undefined, doctorId: undefined, slots: undefined },
      });
    },
    chooseBookingLocation(locationId: string | undefined) {
      dispatch({ type: "booking", patch: { locationId, doctorId: undefined, slots: undefined } });
    },
    chooseBookingDoctor(doctorId: string | undefined) {
      dispatch({ type: "booking", patch: { doctorId, slots: undefined } });
    },
    async loadSlots(from: string, days = 7): Promise<void> {
      const b = stateRef.current.booking;
      if (!b?.typeId) return;
      dispatch({ type: "booking", patch: { busy: true, from, error: undefined } });
      try {
        const slots = await svc.current.api.slots({
          type: b.typeId,
          ...(b.doctorId ? { doctor: b.doctorId } : {}),
          ...(b.locationId ? { location: b.locationId } : {}),
          from,
          days,
        });
        dispatch({ type: "booking", patch: { busy: false, slots } });
      } catch {
        dispatch({ type: "booking", patch: { busy: false, error: "unavailable" } });
      }
    },
    async book(startAt: string, doctorId: string, atNodeId?: string): Promise<boolean> {
      const b = stateRef.current.booking;
      if (!b?.typeId) return false;
      dispatch({ type: "booking", patch: { busy: true, error: undefined } });
      svc.current.tracker?.track("slot_selected", { ...(atNodeId ? { atNodeId } : {}) });
      try {
        const r = await svc.current.api.book({
          consultationTypeId: b.typeId,
          doctorId,
          ...(b.locationId ? { locationId: b.locationId } : {}),
          ...(b.procedures?.length ? { procedures: b.procedures } : {}),
          startAt,
          patientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(atNodeId ? { atNodeId } : {}),
        });
        dispatch({
          type: "booking",
          patch: {
            open: false,
            busy: false,
            booked: {
              typeName: r.type.name,
              doctorName: r.doctor.name,
              startAt: r.startAt,
              endAt: r.endAt,
              timezone: r.timezone,
              mode: r.type.mode,
              status: r.status,
              ...(r.address ? { address: r.address } : {}),
              ...(r.location ? { location: r.location } : {}),
              ...(r.meetingUrl ? { meetingUrl: r.meetingUrl } : {}),
              ...(r.procedures?.length ? { procedures: r.procedures } : {}),
              calendar: r.calendar,
            },
          },
        });
        svc.current.tracker?.track("booking_confirmed", { ...(atNodeId ? { atNodeId } : {}) });
        send({ source: "wapadesk", type: "track", payload: { event: "booking_open" } });
        return true;
      } catch (err) {
        const code = err instanceof ApiClientError ? err.code : "unavailable";
        dispatch({
          type: "booking",
          patch: {
            busy: false,
            error:
              code === "slot_unavailable"
                ? "taken"
                : code === "contact_required"
                  ? "contact"
                  : "unavailable",
            ...(code === "slot_unavailable" ? { slots: undefined } : {}),
          },
        });
        return false;
      }
    },
    /** Paid type: hold the slot and show the card form (11.3). */
    async holdAndPay(startAt: string, doctorId: string, atNodeId?: string): Promise<boolean> {
      const b = stateRef.current.booking;
      if (!b?.typeId) return false;
      dispatch({ type: "booking", patch: { busy: true, error: undefined } });
      svc.current.tracker?.track("slot_selected", { ...(atNodeId ? { atNodeId } : {}) });
      try {
        const r = await svc.current.api.hold({
          consultationTypeId: b.typeId,
          doctorId,
          ...(b.locationId ? { locationId: b.locationId } : {}),
          ...(b.procedures?.length ? { procedures: b.procedures } : {}),
          startAt,
          patientTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(atNodeId ? { atNodeId } : {}),
        });
        svc.current.tracker?.track("payment_started", { ...(atNodeId ? { atNodeId } : {}) });
        dispatch({
          type: "booking",
          patch: { busy: false, payment: { ...r, phase: "pay" } },
        });
        return true;
      } catch (err) {
        const code = err instanceof ApiClientError ? err.code : "unavailable";
        dispatch({
          type: "booking",
          patch: {
            busy: false,
            error:
              code === "slot_unavailable"
                ? "taken"
                : code === "contact_required"
                  ? "contact"
                  : "unavailable",
            ...(code === "slot_unavailable" ? { slots: undefined } : {}),
          },
        });
        return false;
      }
    },
    /** After Stripe.js confirmed the payment: wait for the webhook to confirm the appointment. */
    async awaitPaidConfirmation(): Promise<void> {
      const pay = stateRef.current.booking?.payment;
      if (!pay) return;
      dispatch({ type: "booking", patch: { payment: { ...pay, phase: "confirming" } } });
      for (let i = 0; i < 20; i++) {
        try {
          const st = await svc.current.api.bookingStatus(pay.appointmentId);
          if (st.status === "confirmed" && st.summary) {
            const r = st.summary;
            svc.current.tracker?.track("payment_succeeded");
            svc.current.tracker?.track("booking_confirmed");
            send({ source: "wapadesk", type: "track", payload: { event: "booking_open" } });
            dispatch({
              type: "booking",
              patch: {
                open: false,
                busy: false,
                payment: undefined,
                booked: {
                  typeName: r.type.name,
                  doctorName: r.doctor.name,
                  startAt: r.startAt,
                  endAt: r.endAt,
                  timezone: r.timezone,
                  mode: r.type.mode,
                  status: r.status,
                  ...(r.address ? { address: r.address } : {}),
                  ...(r.location ? { location: r.location } : {}),
                  ...(r.meetingUrl ? { meetingUrl: r.meetingUrl } : {}),
                  calendar: r.calendar,
                },
              },
            });
            return;
          }
          if (st.status === "cancelled") break;
        } catch {
          /* retry */
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      svc.current.tracker?.track("payment_failed");
      dispatch({ type: "booking", patch: { payment: { ...pay, phase: "expired" } } });
    },
    async paymentFailed() {
      const pay = stateRef.current.booking?.payment;
      if (!pay) return;
      // A card error while the hold is still live: let the customer try again. If the 15-minute
      // hold has already been released, say so and send them back to the times.
      let phase: PaymentState["phase"] = "failed";
      try {
        const st = await svc.current.api.bookingStatus(pay.appointmentId);
        if (st.status === "cancelled") phase = "expired";
      } catch {
        /* keep "failed" */
      }
      dispatch({ type: "booking", patch: { payment: { ...pay, phase } } });
    },
    cancelPayment() {
      dispatch({ type: "booking", patch: { payment: undefined, slots: undefined } });
    },
    closeBooking() {
      dispatch({ type: "booking", patch: { open: false, busy: false, error: undefined } });
    },
    async handoff(
      type: "whatsapp" | "booking" | "email",
      atNodeId?: string,
      agentId?: string,
    ): Promise<State["handoff"] | undefined> {
      const s = stateRef.current;
      const early = !s.flow?.completed;
      if (!s.consented) return undefined;
      try {
        const r = await svc.current.api.handoff({
          type,
          ...(atNodeId ? { atNodeId } : {}),
          ...(agentId ? { agentId } : {}),
        });
        if (type !== "email")
          send({
            source: "wapadesk",
            type: "track",
            payload: { event: type === "whatsapp" ? "whatsapp_open" : "booking_open" },
          });
        const h = {
          type,
          url: r.url,
          ...(r.message ? { message: r.message } : {}),
          ...(r.label ? { label: r.label } : {}),
        };
        // Email is sent by the server; keep any earlier WhatsApp handoff on screen.
        if (type !== "email") dispatch({ type: "handoff", handoff: h });
        if (type === "whatsapp") {
          dispatch({ type: "whatsappOpened" });
          svc.current.tracker?.track("whatsapp_click", {
            ...(atNodeId ? { atNodeId } : {}),
            early,
          });
        } else if (type === "booking")
          svc.current.tracker?.track("external_booking_open", {
            ...(atNodeId ? { atNodeId } : {}),
          });
        return h;
      } catch {
        return undefined;
      }
    },
    /** The chat's session token, so the booking page books on the same lead. */
    sessionToken(): string | undefined {
      return svc.current.api.token;
    },
    /** A booking made on the booking page opened from the chat: show it in the conversation. */
    bookedFromPage(r: BookingSummary): void {
      dispatch({
        type: "booking",
        patch: {
          open: false,
          busy: false,
          booked: {
            typeName: r.type.name,
            doctorName: r.doctor.name,
            startAt: r.startAt,
            endAt: r.endAt,
            timezone: r.timezone,
            mode: r.type.mode,
            status: r.status,
            ...(r.address ? { address: r.address } : {}),
            ...(r.location ? { location: r.location } : {}),
            ...(r.meetingUrl ? { meetingUrl: r.meetingUrl } : {}),
            ...(r.procedures?.length ? { procedures: r.procedures } : {}),
            calendar: r.calendar,
          },
        },
      });
      svc.current.tracker?.track("booking_confirmed");
      send({ source: "wapadesk", type: "track", payload: { event: "booking_open" } });
    },
    /** "Continue on your phone" (14b): a short-lived token for the desktop QR code. */
    async handoffLink(atNodeId?: string): Promise<string | undefined> {
      if (!stateRef.current.consented) return undefined;
      try {
        // The scan itself is recorded by the server (whatsapp_click with via "qr").
        const r = await svc.current.api.handoffLink(atNodeId ? { atNodeId } : {});
        return r.token;
      } catch {
        return undefined;
      }
    },
  };

  return { state, actions };
}

import { previewPayload, startBridge } from "./lib/bridge";
import { isPreview, PreviewApi } from "./lib/preview";
function startBridgeOnce(onInit: (p: InitPayload) => void): () => void {
  let done = false;
  return startBridge((p) => {
    if (done) return;
    done = true;
    onInit(p);
  });
}
